/**
 * ============================================================================
 * MASTER SERVICE: Authentication & Security (`tbl_user_master`, `tbl_login_logs`)
 * ============================================================================
 * Purpose:
 *   Handles login, logout, credential validation, and current session inspection.
 *
 * Database Scope:
 *   Master Database (`digimaster`)
 * ============================================================================
 */

import api from "../../core/services/api";

const authService = {
  /**
   * Authenticates a user with username/email and password.
   *
   * @param {Object} credentials - { username, password }
   * @returns {Promise<Object>} Token and user session payload
   */
  login: (credentials) => {
    return api.post("/auth/login", credentials);
  },

  /**
   * Generates a fresh CAPTCHA code and SVG image.
   * @returns {Promise<Object>} { success, captchaId, svg }
   */
  getCaptcha: () => {
    return api.get("/auth/captcha");
  },

  /**
   * Verifies 2FA 6-digit code with temporary token.
   * @param {Object} data - { twoFactorToken, code }
   * @returns {Promise<Object>}
   */
  verify2FA: (data) => {
    return api.post("/auth/verify-2fa", data);
  },

  /**
   * Toggles 2FA for a user account.
   * @param {Object} data - { userId, username, email, enabled }
   * @returns {Promise<Object>}
   */
  toggle2FA: (data) => {
    return api.post("/auth/toggle-2fa", data);
  },

  /**
   * Terminates current user session and clears server-side session state.
   *
   * @returns {Promise<Object>} Logout confirmation
   */
  logout: () => {
    return api.post("/auth/logout");
  },

  /**
   * Validates and returns current authenticated user profile.
   *
   * @returns {Promise<Object>} Current user document
   */
  me: () => {
    return api.get("/auth/me");
  },
};

export default authService;
